import { Component, computed, inject, input } from "@angular/core";

import { AdaaHelper } from "../../../../core/utils";
import { LanguageService } from "../../../services";

export type TimelineBarVariant = "planned" | "actual";

type TimelineBarMarker = {
  ratio: number;
  label: string;
};

type TimelineBarFill = {
  from: number;
  to: number;
};

@Component({
  selector: "adaa-timeline-bar",
  templateUrl: "./timeline-bar.html",
  styleUrl: "./timeline-bar.scss",
})
export class TimelineBarComponent {
  readonly languageService = inject(LanguageService);

  rangeStart = input.required<number>();
  rangeEnd = input.required<number>();
  start = input<number | undefined>(undefined);
  end = input<number | undefined>(undefined);
  variant = input<TimelineBarVariant>("planned");

  markers = computed<TimelineBarMarker[]>(() => {
    this.languageService.current();

    return [this.start(), this.end()]
      .filter((epoch): epoch is number => !!epoch)
      .map((epoch) => ({ ratio: this._ratio(epoch), label: this._label(epoch) }));
  });

  fill = computed<TimelineBarFill | undefined>(() => {
    const [first, second] = this.markers();
    if (!first || !second) return undefined;

    return {
      from: Math.min(first.ratio, second.ratio),
      to: Math.max(first.ratio, second.ratio),
    };
  });

  /**
   * @description
   *  Two dates in the same month would print the same label on top of each other, so they
   *  share one label placed between the markers.
   */
  labels = computed<TimelineBarMarker[]>(() => {
    const [first, second] = this.markers();
    if (!second) return this.markers();
    if (first.label !== second.label) return this.markers();

    return [{ ratio: (first.ratio + second.ratio) / 2, label: first.label }];
  });

  private _ratio(epoch: number): number {
    const span = this.rangeEnd() - this.rangeStart();
    if (span <= 0) return 0;

    const ratio = (epoch - this.rangeStart()) / span;
    return Math.min(1, Math.max(0, ratio));
  }

  private _label(epoch: number): string {
    const label = AdaaHelper.formatDate(epoch, false, { year: "numeric", month: "short" });
    return typeof label === "string" ? label : "";
  }
}
